import React from 'react';
import { FileText, CheckCircle, Plus, X, Edit, Printer } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { writeSanitizedHTML } from '../../utils/safePrint';

/**
 * ApprovalRouteForm Component
 * Handles the Approval & Routing Information form (part of ICS Status Summary)
 */
const ApprovalRouteForm = ({
  theme,
  positions,
  // State from useApprovalRoute hook
  approvalRoutes,
  currentApprovalRoute,
  setCurrentApprovalRoute,
  editingApprovalRoute,
  // Functions from useApprovalRoute hook
  addApprovalRoute,
  editApprovalRoute,
  cancelEditApprovalRoute,
  deleteApprovalRoute,
  // Utility
  format24HourTime
}) => {
  const printSingleRoute = (route) => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <html>
        <head>
          <title>Approval & Routing Information Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
            .header h1 { color: #f97316; margin: 0; font-size: 28px; }
            .section { margin-bottom: 25px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; }
            .section h3 { color: #f97316; margin-top: 0; border-bottom: 2px solid #f97316; padding-bottom: 5px; }
            .field-group { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 15px; }
            .field { margin-bottom: 10px; }
            .field-label { font-weight: bold; color: #f97316; margin-bottom: 3px; }
            .field-value { color: #333; }
            .prepared-section { background-color: #fff5f5; }
            .approved-section { background-color: #f0fdf4; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Approval & Routing Information</h1>
            <p><strong>Entry ID:</strong> ${route.id} | <strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${format24HourTime()}</p>
          </div>
          
          <div class="section prepared-section">
            <h3>Prepared By</h3>
            <div class="field-group">
              <div class="field">
                <div class="field-label">Name:</div>
                <div class="field-value">${route.preparedByName}</div>
              </div>
              <div class="field">
                <div class="field-label">ICS Position:</div>
                <div class="field-value">${route.preparedByPosition || 'Not specified'}</div>
              </div>
            </div>
            <div class="field-group">
              <div class="field">
                <div class="field-label">Date/Time Prepared:</div>
                <div class="field-value">${route.preparedDate} ${route.preparedTime}</div>
              </div>
              <div class="field">
                <div class="field-label">Submitted:</div>
                <div class="field-value">${route.submittedDate} ${route.submittedTime}</div>
              </div>
            </div>
          </div>

          <div class="section approved-section">
            <h3>Approved By</h3>
            <div class="field-group">
              <div class="field">
                <div class="field-label">Name:</div>
                <div class="field-value">${route.approvedByName || 'Pending'}</div>
              </div>
              <div class="field">
                <div class="field-label">ICS Position:</div>
                <div class="field-value">${route.approvedByPosition || 'Not specified'}</div>
              </div>
            </div>
            <div class="field-group">
              <div class="field">
                <div class="field-label">Date/Time Approved:</div>
                <div class="field-value">${route.approvedDate} ${route.approvedTime}</div>
              </div>
              <div class="field">
                <div class="field-label">Primary Location / Org / Agency sent to:</div>
                <div class="field-value">${route.primaryLocation || 'Not specified'}</div>
              </div>
            </div>
          </div>
          
          <div class="footer">
            <p>Powered by EXRSIM - Emergency Response Simulation Platform</p>
          </div>
        </body>
      </html>
    `;
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => printWindow.print(), 250);
  };

  const printAllRoutes = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <html>
        <head>
          <title>Approval & Routing Information Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
            .header h1 { color: #f97316; margin: 0; font-size: 28px; }
            .route-item { margin-bottom: 30px; page-break-inside: avoid; border: 1px solid #ddd; border-radius: 8px; padding: 20px; }
            .section { margin-bottom: 20px; }
            .section h4 { color: #f97316; margin-bottom: 10px; border-bottom: 1px solid #f97316; padding-bottom: 3px; }
            .field-group { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 15px; }
            .field { margin-bottom: 10px; }
            .field-label { font-weight: bold; color: #f97316; margin-bottom: 3px; }
            .field-value { color: #333; }
            .prepared-section { background-color: #fff5f5; padding: 15px; border-radius: 5px; }
            .approved-section { background-color: #f0fdf4; padding: 15px; border-radius: 5px; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Approval & Routing Information Report</h1>
            <p><strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${format24HourTime()} | <strong>Total Routes:</strong> ${approvalRoutes.length}</p>
          </div>
          
          ${approvalRoutes.map((route, index) => `
            <div class="route-item">
              <h3 style="color: #f97316; margin-top: 0;">Approval Route #${index + 1}</h3>
              
              <div class="section prepared-section">
                <h4>Prepared By</h4>
                <div class="field-group">
                  <div class="field">
                    <div class="field-label">Name:</div>
                    <div class="field-value">${route.preparedByName}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">ICS Position:</div>
                    <div class="field-value">${route.preparedByPosition || 'Not specified'}</div>
                  </div>
                </div>
                <div class="field-group">
                  <div class="field">
                    <div class="field-label">Date/Time Prepared:</div>
                    <div class="field-value">${route.preparedDate} ${route.preparedTime}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">Submitted:</div>
                    <div class="field-value">${route.submittedDate} ${route.submittedTime}</div>
                  </div>
                </div>
              </div>

              <div class="section approved-section">
                <h4>Approved By</h4>
                <div class="field-group">
                  <div class="field">
                    <div class="field-label">Name:</div>
                    <div class="field-value">${route.approvedByName || 'Pending'}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">ICS Position:</div>
                    <div class="field-value">${route.approvedByPosition || 'Not specified'}</div>
                  </div>
                </div>
                <div class="field-group">
                  <div class="field">
                    <div class="field-label">Date/Time Approved:</div>
                    <div class="field-value">${route.approvedDate} ${route.approvedTime}</div>
                  </div>
                  <div class="field">
                    <div class="field-label">Primary Location / Org:</div>
                    <div class="field-value">${route.primaryLocation || 'Not specified'}</div>
                  </div>
                </div>
              </div>
            </div>
          `).join('')}
          
          <div class="footer">
            <div><strong>Approval & Routing Information Report</strong> - Total: ${approvalRoutes.length} entries</div>
            <div>Status Summary: ${approvalRoutes.filter(r => r.approvedByName).length} approved, ${approvalRoutes.filter(r => !r.approvedByName).length} pending</div>
            <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
          </div>
        </body>
      </html>
    `;
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div className="p-6 space-y-6 pb-20">
      {/* Approval & Routing Information Header */}
      <div className="text-center mb-8">
        <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Approval & Routing Information</h1>
        <p className={`${theme.colors.textMuted} text-lg`}>
          Document approval workflows, routing procedures, and authorization tracking for incident operations
        </p>
      </div>

      {/* Approval & Routing Information Form */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader>
          <CardTitle className="text-orange-500">Approval & Routing Information Form</CardTitle>
          <CardDescription className={`${theme.colors.textMuted}`}>
            Enter preparation, submission, and approval details with routing information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Prepared By Section */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-orange-500" />
              Prepared By
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Name *</label>
                <Input
                  type="text"
                  value={currentApprovalRoute.preparedByName}
                  onChange={(e) => setCurrentApprovalRoute(prev => ({...prev, preparedByName: e.target.value}))}
                  placeholder="Enter preparer's name"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Position</label>
                <Select 
                  value={currentApprovalRoute.preparedByPosition || ""} 
                  onValueChange={(value) => setCurrentApprovalRoute(prev => ({...prev, preparedByPosition: value}))}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600 max-h-60">
                    {positions.map((position) => (
                      <SelectItem 
                        key={position} 
                        value={position} 
                        className="text-white focus:bg-gray-600"
                      >
                        {position}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Date/Time Prepared - Date/Time (24 hour)</label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={currentApprovalRoute.preparedDate}
                    onChange={(e) => setCurrentApprovalRoute(prev => ({...prev, preparedDate: e.target.value}))}
                    className="bg-gray-800 border-gray-600 text-white flex-1"
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    maxLength="5"
                    value={currentApprovalRoute.preparedTime}
                    onChange={(e) => {
                      let v = e.target.value.replace(/[^0-9:]/g, '');
                      if (v.length === 2 && !v.includes(':')) v = v + ':';
                      if (v.length <= 5) setCurrentApprovalRoute(prev => ({...prev, preparedTime: v}));
                    }}
                    className="bg-gray-800 border-gray-600 text-white w-24"
                  />
                </div>
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Submitted - Date/Time (24 hour)</label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={currentApprovalRoute.submittedDate}
                    onChange={(e) => setCurrentApprovalRoute(prev => ({...prev, submittedDate: e.target.value}))}
                    className="bg-gray-800 border-gray-600 text-white flex-1"
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    maxLength="5"
                    value={currentApprovalRoute.submittedTime}
                    onChange={(e) => {
                      let v = e.target.value.replace(/[^0-9:]/g, '');
                      if (v.length === 2 && !v.includes(':')) v = v + ':';
                      if (v.length <= 5) setCurrentApprovalRoute(prev => ({...prev, submittedTime: v}));
                    }}
                    className="bg-gray-800 border-gray-600 text-white w-24"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Approved By Section */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <CheckCircle className="h-5 w-5 mr-2 text-green-500" />
              Approved By
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Name</label>
                <Input
                  type="text"
                  value={currentApprovalRoute.approvedByName}
                  onChange={(e) => setCurrentApprovalRoute(prev => ({...prev, approvedByName: e.target.value}))}
                  placeholder="Enter approver's name"
                  className="bg-gray-800 border-gray-600 text-white"
                />
              </div>
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Position</label>
                <Select 
                  value={currentApprovalRoute.approvedByPosition || ""} 
                  onValueChange={(value) => setCurrentApprovalRoute(prev => ({...prev, approvedByPosition: value}))}
                >
                  <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600 max-h-60">
                    {positions.map((position) => (
                      <SelectItem 
                        key={position} 
                        value={position} 
                        className="text-white focus:bg-gray-600"
                      >
                        {position}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-white text-sm font-medium mb-2 block">Submitted - Date/Time (24 hour)</label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={currentApprovalRoute.approvedDate}
                    onChange={(e) => setCurrentApprovalRoute(prev => ({...prev, approvedDate: e.target.value}))}
                    className="bg-gray-800 border-gray-600 text-white flex-1"
                  />
                  <Input
                    type="text"
                    placeholder="HH:MM"
                    maxLength="5"
                    value={currentApprovalRoute.approvedTime}
                    onChange={(e) => {
                      let v = e.target.value.replace(/[^0-9:]/g, '');
                      if (v.length === 2 && !v.includes(':')) v = v + ':';
                      if (v.length <= 5) setCurrentApprovalRoute(prev => ({...prev, approvedTime: v}));
                    }}
                    className="bg-gray-800 border-gray-600 text-white w-24"
                  />
                </div>
              </div>
            </div>
            <div className="mt-4">
              <label className="text-white text-sm font-medium mb-2 block">Primary Location / Org / Agency sent to:</label>
              <Input
                type="text"
                value={currentApprovalRoute.primaryLocation}
                onChange={(e) => setCurrentApprovalRoute(prev => ({...prev, primaryLocation: e.target.value}))}
                placeholder="Enter primary location, organization or agency"
                className="bg-gray-800 border-gray-600 text-white"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={addApprovalRoute} className="bg-orange-500 hover:bg-orange-600 text-white">
              <Plus className="h-4 w-4 mr-2" />
              {editingApprovalRoute ? 'Update Approval Route' : 'Add Approval Route'}
            </Button>
            {editingApprovalRoute && (
              <Button onClick={cancelEditApprovalRoute} variant="outline" className="border-gray-500 text-gray-300">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Display Approval Routes */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader>
          <CardTitle className="text-orange-500">Approval & Routing Records ({approvalRoutes.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {approvalRoutes.map(route => (
              <div key={route.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                {/* Prepared By Info */}
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                    <FileText className="h-4 w-4 mr-2 text-orange-500" />
                    Prepared By
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <div className="text-orange-500 font-semibold mb-1">Name</div>
                      <div className="text-white">{route.preparedByName}</div>
                    </div>
                    <div>
                      <div className="text-orange-500 font-semibold mb-1">ICS Position</div>
                      <div className="text-white">{route.preparedByPosition || 'Not specified'}</div>
                    </div>
                    <div>
                      <div className="text-orange-500 font-semibold mb-1">Date/Time Prepared</div>
                      <div className="text-white">{route.preparedDate} {route.preparedTime}</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="text-orange-500 font-semibold mb-1">Submitted</div>
                    <div className="text-white">{route.submittedDate} {route.submittedTime}</div>
                  </div>
                </div>

                {/* Approved By Info */}
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                    Approved By
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <div className="text-orange-500 font-semibold mb-1">Name</div>
                      <div className="text-white">{route.approvedByName || 'Pending'}</div>
                    </div>
                    <div>
                      <div className="text-orange-500 font-semibold mb-1">ICS Position</div>
                      <div className="text-white">{route.approvedByPosition || 'Not specified'}</div>
                    </div>
                    <div>
                      <div className="text-orange-500 font-semibold mb-1">Date/Time Approved</div>
                      <div className="text-white">{route.approvedDate} {route.approvedTime}</div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="text-orange-500 font-semibold mb-1">Primary Location / Org / Agency sent to:</div>
                    <div className="text-white">{route.primaryLocation || 'Not specified'}</div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => editApprovalRoute(route)}
                    variant="ghost"
                    size="sm"
                    className="text-blue-400 hover:text-blue-300"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => printSingleRoute(route)}
                    variant="ghost"
                    size="sm"
                    className="text-orange-400 hover:text-orange-300"
                  >
                    <Printer className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {approvalRoutes.length > 0 && (
              <Button 
                onClick={printAllRoutes}
                variant="outline" 
                className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
              >
                <FileText className="h-4 w-4 mr-2" />
                Print All Approval Routes
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ApprovalRouteForm;
